import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ButtonComponent } from '@modules/components/button/button.component';
import { CheckboxComponent } from '@modules/components/checkbox/checkbox.component';
import { ChipComponent } from '@modules/components/chip/chip.component';
import { DIALOG_DIRECTIVES } from '@modules/components/dialog/dialog';
import { LoaderComponent } from '@modules/components/loader/loader.component';
import { SearchFieldComponent } from '@modules/components/search-field/search-field.component';
import { SelectComponent } from '@modules/components/select/select.component';
import { SelectOption } from '@modules/components/select/select.type';
import { SnackBarComponent } from '@modules/components/snack-bar/snack-bar.component';
import { FullNamePipe } from '@modules/pipes/full-name.pipe';
import { UserType } from '@type/user.type';
import { UsersService } from '../../services/users/users.service';

@Component({
  selector: 'app-copy-user-settings-dialog',
  imports: [
    ...DIALOG_DIRECTIVES,
    SelectComponent,
    SearchFieldComponent,
    CheckboxComponent,
    ChipComponent,
    LoaderComponent,
    ButtonComponent,
    MatIcon,
    FullNamePipe,
  ],
  templateUrl: './copy-user-settings-dialog.component.html',
  styleUrls: ['./copy-user-settings-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopyUserSettingsDialogComponent {
  private dialogRef = inject(DialogRef);

  private data = inject<{ companyId: number }>(DIALOG_DATA);

  private usersService = inject(UsersService);

  private snack = inject(MatSnackBar);

  loading = signal(true);

  allUsers = signal<UserType[]>([]);

  donorId = signal<number | null>(null);

  donor = computed(() => this.allUsers().find(({ id }) => id === this.donorId()) ?? null);

  donorOptions = computed((): SelectOption<number>[] =>
    this.allUsers().map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
      description: user.email,
    })),
  );

  selectedIds = signal(new Set<number>());

  recipientSearch = signal<string | null>('');

  filteredRecipients = computed(() => {
    const donorId = this.donor()?.id;
    const search = (this.recipientSearch() ?? '').toLowerCase().trim();
    return this.allUsers().filter((u) => u.id !== donorId && this.matchesSearch(u, search));
  });

  isAllSelected = computed(() => {
    const filtered = this.filteredRecipients();
    if (!filtered.length) return false;
    const ids = this.selectedIds();
    return filtered.every((u) => ids.has(u.id));
  });

  isIndeterminate = computed(() => {
    const filtered = this.filteredRecipients();
    const ids = this.selectedIds();
    const count = filtered.filter((u) => ids.has(u.id)).length;
    return count > 0 && count < filtered.length;
  });

  selectedCount = computed(() => this.selectedIds().size);

  canCopy = computed(() => this.donor() !== null && this.selectedIds().size > 0);

  constructor() {
    this.usersService
      .getUsersList({ per_page: 10000, page: 1, q: {}, sorting: '' }, this.data.companyId)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: ({ data }) => {
          this.allUsers.set(data as UserType[]);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  selectDonor(donorId: number | null): void {
    this.donorId.set(donorId);

    if (donorId === null) {
      this.recipientSearch.set('');
      this.selectedIds.set(new Set());
      return;
    }

    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      next.delete(donorId);
      return next;
    });
  }

  toggleRecipient(userId: number): void {
    if (!this.donor()) return;
    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  toggleAll(): void {
    const filtered = this.filteredRecipients();
    if (this.isAllSelected()) {
      this.selectedIds.update((ids) => {
        const next = new Set(ids);
        filtered.forEach((u) => next.delete(u.id));
        return next;
      });
    } else {
      this.selectedIds.update((ids) => {
        const next = new Set(ids);
        filtered.forEach((u) => next.add(u.id));
        return next;
      });
    }
  }

  copy(): void {
    const donor = this.donor();
    if (!donor || !this.selectedIds().size) return;

    this.usersService.copyUserSettings(this.data.companyId, donor.id, [...this.selectedIds()]).subscribe({
      next: ({ succeeded_count, failed_count }) => {
        const message =
          failed_count === 0 ?
            `Settings copied to ${succeeded_count} member(s) successfully`
          : `Copied: ${succeeded_count}, failed: ${failed_count}`;
        this.snack.openFromComponent(SnackBarComponent, {
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          data: { message },
          duration: 5000,
        });
        this.dialogRef.close();
      },
    });
  }

  private matchesSearch(user: UserType, search: string): boolean {
    if (!search) return true;
    return (
      user.first_name?.toLowerCase().includes(search) ||
      user.last_name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  }
}
